"use client";

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../lib/utils';

/**
 * A short label for a control whose name isn't visible (an icon button, a
 * compact navigation item). It opens on hover and keyboard focus, closes on
 * Escape, and is never the only place essential information lives.
 * Each Tooltip carries its own provider, so it works anywhere; wrap a group
 * (a navigation rail) in TooltipProvider so moving between them is instant.
 */
const InsideProvider = React.createContext(false);

const TooltipProvider = ({
  delayDuration = 300,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>) => (
  <InsideProvider.Provider value={true}>
    <TooltipPrimitive.Provider delayDuration={delayDuration} {...props} />
  </InsideProvider.Provider>
);

function Tooltip({
  delayDuration = 300,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>) {
  const grouped = React.useContext(InsideProvider);
  if (grouped) return <TooltipPrimitive.Root delayDuration={delayDuration} {...props} />;
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>
      <TooltipPrimitive.Root {...props} />
    </TooltipPrimitive.Provider>
  );
}
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, collisionPadding = 8, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      className={cn(
        'z-[120] max-w-xs rounded-md bg-tooltip px-2.5 py-1.5 text-xs font-medium leading-5 text-tooltip-foreground shadow-md',
        'data-[state=delayed-open]:animate-fade-in data-[state=instant-open]:animate-fade-in data-[state=closed]:animate-fade-out',
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));

TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/** A tooltip around one trigger, for the common case. */
function SimpleTooltip({
  content,
  side,
  align,
  children,
}: {
  content: React.ReactNode;
  side?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>['side'];
  align?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>['align'];
  children: React.ReactElement;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} align={align}>
        {content}
      </TooltipContent>
    </Tooltip>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, SimpleTooltip };
